import { useGetActiveLeadSourceDetails } from '../../hooks'
import CampaignService from '@forwardflow/services/CampaignService'
import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { ConversationalAI } from './ConversationalAI'

import { message } from 'antd'
import { useState } from 'react'
import { usePrompts } from '@forwardflow/hooks/customHooks/usePrompts'
import useSetupProgress from '@forwardflow/hooks/customHooks/onboarding/useSetupProgress'
import { useAddAssistantsModalStore } from '@forwardflow/store/modal/AddAssistantsModal'
import VoiceAvatar1 from '@forwardflow/assets/onboarding/voice1.png'
import cn from 'classnames'

interface AssistantsListItemProps {
   name: string
   title: string
   description: string
   designation: string
   isEdited: boolean
   assistant_id: number
   avatar?: string
   gender?: string
   accent?: string
   voice_id: string
   onRemoveCampaignAssistant?: () => void
   onUpdateAssistantName?: (newName: string) => void
}

export const AssistantsListItem: React.FC<AssistantsListItemProps> = ({
   name,
   title,
   description,
   designation,
   assistant_id,
   avatar,
   voice_id,
   onRemoveCampaignAssistant,
   onUpdateAssistantName,
}) => {
   const [isEditingName, setIsEditingName] = useState(false)
   const [isNameUpdated, setIsNameUpdated] = useState(false)
   const [newName, setNewName] = useState(name)

   const [isConversationActive, setIsConversationActive] = useState(false)
   const [stopConversation, setStopConversation] = useState(false)

   const { mutate, isLoading: isLoadingAssistant } =
      useGetActiveLeadSourceDetails(title as string)

   const { conversationalAiPrompt } = usePrompts()
   const setupProgress = useSetupProgress()

   const { isAddAssistantsModalOpen } = useAddAssistantsModalStore()

   const handleEditName = () => {
      setStopConversation(true)
      setTimeout(() => setStopConversation(false), 100)
      setIsEditingName(true)
   }

   const handleSaveName = async () => {
      if (onUpdateAssistantName && newName.trim()) {
         onUpdateAssistantName(newName.trim())
      }
      try {
         setIsNameUpdated(true)
         await CampaignService.updateOrganizationCampaignAssistants(
            title as string,
            assistant_id as number,
            {
               assistantName: newName,
               description: description,
               voiceId: voice_id,
               avatar: avatar,
               designation: designation,
            }
         )
         message.success('Assistant updated successfully')
         mutate()
         setIsEditingName(false)
      } catch (error) {
         message.error('Error updating assistant')
      } finally {
         setIsNameUpdated(false)
         mutate()
      }
   }

   const handleCancelEdit = () => {
      setNewName(name)
      setIsEditingName(false)
   }

   const dynamicVariables = {
      organization_name:
         setupProgress.data?.businessDetails?.legalBusinessName ||
         'Organization',
      name: name || 'Assistant',
   }

   return (
      <div className="relative flex items-center gap-6 px-10 py-4 transition-all bg-primary/10 hover:bg-primary/20 group">
         {isEditingName && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/90">
               <div className="flex items-center w-full h-full py-6 pl-11 pr-10 bg-[#4a3f66]">
                  <div className="flex flex-col mr-auto">
                     <label className="mb-2 font-medium text-[12px]">
                        Edit Assistant Name
                     </label>
                     <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        className="w-full px-4 rounded bg-[#828282]/10 border border-white/10 leading-none h-10 outline-none"
                        placeholder="Enter new name"
                        autoFocus
                     />
                  </div>

                  <div className="flex gap-4">
                     <Button
                        isLoading={isNameUpdated}
                        onClick={handleSaveName}
                        className="!h-10 !m-0 !w-[140px] px-4 font-medium leading-none text-white !rounded bg-primary/60 hover:bg-primary/80"
                     >
                        Change name
                     </Button>
                     <button onClick={handleCancelEdit}>
                        <Icon name="Close" className="fill-white"></Icon>
                     </button>
                  </div>
               </div>
            </div>
         )}

         <div className="w-[68px] h-[68px] shrink-0 rounded bg-transparent flex justify-center items-end relative group overflow-hidden conversational-ai-area">
            <img
               src={VoiceAvatar1}
               alt={voice_id}
               className={cn(
                  'relative transition-all duration-300',
                  isConversationActive && 'scale-105'
               )}
               loading="lazy"
            />

            <div
               className={`absolute inset-0 items-center justify-center bg-transparent ${
                  isConversationActive ? 'flex' : 'hidden group-hover:flex'
               }`}
            >
               {!stopConversation && (
                  <ConversationalAI
                     voiceId={voice_id}
                     prompt={conversationalAiPrompt?.prompt ?? ''}
                     dynamicVariables={dynamicVariables}
                     onConversationStateChange={setIsConversationActive}
                     shouldStop={isAddAssistantsModalOpen}
                  />
               )}
            </div>
         </div>

         <div className="conversational-ai-area">
            <div className="flex items-center mb-3 gap-x-2">
               <p className="font-medium">{name}</p>
               <p className="p-1 px-2 bg-black/20 text-[12px] text-white/70 uppercase rounded font-medium">
                  {designation === 'SALES_REPRESENTATIVE'
                     ? 'Sales assistant'
                     : designation}
               </p>
            </div>
            <p className="text-white/70 text-[12px] font-medium line-clamp-2">
               {description}
            </p>
         </div>

         <div className="flex items-center ml-auto gap-x-2">
            <button
               disabled={isLoadingAssistant}
               onClick={handleEditName}
               className="hidden h-10 ml-auto rounded group-hover:block bg-primary/20 w-28 hover:bg-primary/30"
            >
               Edit name
            </button>
            <Button
               isDisabled={isLoadingAssistant}
               onClick={() => {
                  setStopConversation(true)
                  setTimeout(() => setStopConversation(false), 100)
                  onRemoveCampaignAssistant?.()
               }}
               className="!h-10 !m-0 shrink-0 group-hover:block hidden transition-all border !rounded !w-28 !border-[#C11574] !bg-[#C11574]/20 hover:!bg-[#C11574]/40"
            >
               Remove
            </Button>
         </div>
      </div>
   )
}
